<template>
  <div
    v-if="!closed"
    :class="`ds-toast ds-toast-${themeColor}`"
    data-testid="ds-toast"
    @mouseenter="pausedTimer(true)"
    @mouseleave="pausedTimer(false)"
  >
    <div class="ds-toast-container">
      <DsComponentRender v-if="component" :component="component" />
      <template v-else>
        <!-- Icon -->
        <Component :is="currentIcon" class="ds-toast-icon" />
        <!-- message -->
        <div class="ds-toast-content">
          <h6 class="ds-toast-title" data-testid="ds-toast-title">
            {{ title }}
          </h6>
          <p class="ds-toast-message" data-testid="ds-toast-message">
            {{ message }}
          </p>
        </div>
      </template>

      <!-- close -->
      <DsCloseOutlineIcon
        v-if="enableClose"
        class="ds-toast-close"
        data-testid="ds-toast-close"
        @click="onClose"
      />
    </div>
    <!-- animation -->
    <div
      v-if="duration > 0"
      class="ds-toast-progress-bar-wrap"
      data-testid="ds-toast-progress-bar-wrap"
    >
      <div
        :class="[
          'ds-toast-progress-bar',
          `ds-toast-progress-bar-${themeColor}`,
          {
            'ds-toast-animation-paused': isPaused,
            'ds-toast-animation-resume': !isPaused,
          },
        ]"
        :style="animation"
        data-testid="ds-toast-progress-bar"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import { DsNotifier } from "../../helpers";
import type { DsComponentRenderProp } from "../component-render";
import { getUnixTime } from "date-fns";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";

import DsComponentRender from "../component-render/DsComponentRender.vue";

import {
  DsAlertSolidIcon,
  DsCircleCheckSolidIcon,
  DsCloseOutlineIcon,
  DsExclamationSolidIcon,
  DsInfoCircleOutlineIcon,
} from "../icon";

import type { DsToastTheme } from "../../helpers/toast/DsNotifier.types";

defineOptions({
  name: "DsToast",
});

const { t } = useI18n();
const instance = getCurrentInstance();

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  message: {
    type: String,
    required: false,
  },
  themeColor: {
    type: String as PropType<DsToastTheme>,
    default: "blue",
  },
  duration: {
    type: Number,
    default: 10,
  },
  enableClose: {
    type: Boolean,
    default: true,
  },
  component: {
    type: Object as PropType<DsComponentRenderProp>,
    required: false,
  },
});

const animation = computed((): string => {
  return `animation: ${props.duration}s linear ds-toast-theme-color, ${props.duration}s ease-out ds-toast-enter forwards`;
});

const title = computed((): string => {
  if (props.title && props.title?.length > 0) {
    return props.title;
  }
  if (props.themeColor === "green") {
    return t("ds.component.toast.success");
  }
  if (props.themeColor === "yellow") {
    return t("ds.component.toast.warning");
  }
  if (props.themeColor === "red") {
    return t("ds.component.toast.error");
  }

  return t("ds.component.toast.information");
});

const currentIcon = computed((): DsComponentRenderProp => {
  if (props.themeColor === "green") {
    return DsCircleCheckSolidIcon;
  }
  if (props.themeColor === "yellow") {
    return DsExclamationSolidIcon;
  }
  if (props.themeColor === "red") {
    return DsAlertSolidIcon;
  }

  return DsInfoCircleOutlineIcon;
});

const closed = ref(false);
const isPaused = ref(false);
const timer = {
  id: 0,
  rest: 0,
  start: 0,
  pause: 0,
};

const onClose = () => {
  closed.value = true;
  DsNotifier.delete(instance?.vnode.key as number);
};

const timerClock = () => {
  return window.setTimeout(() => {
    onClose();
  }, timer.rest * 1000);
};

onMounted(() => {
  timer.start = getUnixTime(new Date());
  timer.pause = timer.start;
  timer.rest = props.duration as number;

  if (props.duration > 0) {
    timer.id = timerClock();
  }
});

const pausedTimer = (flag: boolean): void => {
  //Don't manage timer if set duration equal to 0
  if (props.duration === 0) {
    return;
  }

  isPaused.value = flag;
  if (isPaused.value) {
    clearTimeout(timer.id);
    timer.pause = getUnixTime(new Date()) - timer.start;
    timer.rest = timer.rest - timer.pause;
  } else {
    timer.start = getUnixTime(new Date());
    timer.id = timerClock();
  }
};

onBeforeUnmount(() => {
  clearTimeout(timer.id);
});
</script>

<style lang="scss">
// We don't use scoped attribute because the animation doesn't apply
@keyframes ds-toast-theme-color {
  100% {
    background-position: 0;
  }
  0% {
    background-position: 100%;
  }
}

@keyframes ds-toast-enter {
  100% {
    transform: scaleX(0);
  }
  0% {
    transform: scaleX(1);
  }
}
</style>

<style lang="scss" scoped>
.ds-toast {
  @apply flex flex-col max-w-[300px] md:max-w-md h-auto border-l-2 p-4 rounded-r-md bg-white shadow;

  &-container {
    @apply flex justify-between gap-x-[18px];
  }

  &-icon {
    @apply flex-none w-5 h-5;
  }

  &-content {
    @apply col-span-2 space-y-1 flex flex-col items-start w-[300px] md:w-[320px] break-words line-clamp-6;
  }

  &-title {
    @apply text-sm font-medium text-gray-900 w-full;
  }

  &-message {
    @apply text-sm font-normal text-gray-500 w-full;
  }

  &-close {
    @apply cursor-pointer text-gray-500 hover:text-gray-900 h-5 w-5 ml-auto;
  }

  &-blue {
    @apply border-l-blue-500;

    .ds-toast-icon {
      @apply text-blue-500;
    }
  }

  &-green {
    @apply border-l-green-500;

    .ds-toast-icon {
      @apply text-green-500;
    }
  }

  &-yellow {
    @apply border-l-yellow-500;

    .ds-toast-icon {
      @apply text-yellow-500;
    }
  }

  &-red {
    @apply border-l-red-500;

    .ds-toast-icon {
      @apply text-red-500;
    }
  }

  &-gray {
    @apply border-l-gray-500;

    .ds-toast-icon {
      @apply text-gray-500;
    }
  }

  &-black {
    @apply border-l-gray-900;

    .ds-toast-icon {
      @apply text-gray-900;
    }
  }

  &-white {
    @apply border-l-gray-200;

    .ds-toast-icon {
      @apply text-gray-200;
    }
  }
  &-indigo {
    @apply border-l-indigo-500;

    .ds-toast-icon {
      @apply text-indigo-500;
    }
  }

  &-pink {
    @apply border-l-pink-500;

    .ds-toast-icon {
      @apply text-pink-500;
    }
  }

  &-orange {
    @apply border-l-orange-500;

    .ds-toast-icon {
      @apply text-orange-500;
    }
  }

  &-teal {
    @apply border-l-teal-500;

    .ds-toast-icon {
      @apply text-teal-500;
    }
  }

  &-progress-bar {
    @apply bg-gray-300 h-0.5 origin-right;

    &-blue {
      @apply bg-gradient-to-r from-blue-600 to-blue-300;
    }

    &-green {
      @apply bg-gradient-to-r from-green-600 to-green-300;
    }

    &-yellow {
      @apply bg-gradient-to-r from-yellow-600 to-yellow-300;
    }

    &-red {
      @apply bg-gradient-to-r from-red-500 to-red-300;
    }

    &-gray {
      @apply bg-gradient-to-r from-black to-gray-300;
    }

    &-black {
      @apply bg-gradient-to-r from-black to-gray-900;
    }

    &-white {
      @apply bg-gradient-to-r from-black to-gray-100;
    }

    &-indigo {
      @apply bg-gradient-to-r from-black to-indigo-300;
    }

    &-pink {
      @apply bg-gradient-to-r from-black to-pink-300;
    }

    &-orange {
      @apply bg-gradient-to-r from-black to-orange-300;
    }

    &-teal {
      @apply bg-gradient-to-r from-black to-teal-300;
    }

    &-wrap {
      @apply w-auto bg-gray-200 mt-2;
    }
  }

  &-animation-paused {
    animation-play-state: paused !important;
  }

  &-animation-resume {
    animation-play-state: running !important;
  }
}
</style>
